import type { ReactNode } from "react";
import { DisclosureChevron, ExpandIn } from "@/components/common/Disclosure";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { StatusDot as CommonStatusDot, type StatusDotTone } from "@/components/common/StatusDot";
import { DiffFileLink } from "@/components/diff/DiffFileLink";
import { DiffHunkList } from "@/components/diff/DiffHunkList";
import { DiffLineStats } from "@/components/diff/DiffLineStats";
import { countDiffLines, type ToolCallStatus } from "@/lib/chat";
import { isBrowserTool } from "@/lib/browser-tool-labels";
import type { ToolUseRenderBlock } from "@/lib/chat-render";
import { MessageImages } from "./MessageImages";
import { hasToolDetails, toolCopyText } from "./tool-entry-label";
import { ToolEntryLabel } from "./ToolEntryLabel";

/**
 * ツール行の丸。アクセントは実行中だけに使い、完了は落ち着いたグレー、取り消し・
 * 省略は完了と見分けられるよう一段暗いグレーにする。
 */
const DOT_TONE: Record<ToolCallStatus, StatusDotTone> = {
	running: "info",
	completed: "muted",
	waiting_user: "warning",
	error: "danger",
	cancelled: "disabled",
	skipped: "disabled",
};

export const CODE_BOX_CLASS =
	"m-0 rounded-2 p-2 font-mono text-caption leading-normal text-fg-2 bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-1)]";

export const PRE_CLASS = `${CODE_BOX_CLASS} max-h-[300px] overflow-auto whitespace-pre`;

export const HEADER_BUTTON_CLASS =
	"inline-flex items-center gap-1.5 max-w-full p-0 bg-transparent border-0 text-left text-ui leading-body text-fg-3 [font-family:inherit] cursor-pointer disabled:cursor-default";

export function DetailBox({
	label,
	copyLabel,
	copyValue,
	children,
}: {
	label: string;
	copyLabel: string;
	copyValue: string;
	children: ReactNode;
}) {
	return (
		<div className="min-w-0">
			<div className="flex items-center justify-between mb-1">
				<span className="text-section font-semibold tracking-label uppercase text-fg-3">
					{label}
				</span>
				{copyValue && <CopyIconButton value={copyValue} label={copyLabel} />}
			</div>
			{children}
		</div>
	);
}

export function OutputBox({ output }: { output: string }) {
	return (
		<DetailBox label="出力" copyLabel="出力をコピー" copyValue={output}>
			<pre className={`select-text ${PRE_CLASS}`}>{output}</pre>
		</DetailBox>
	);
}

function InputBox({ input }: { input: Record<string, unknown> }) {
	if (Object.keys(input).length === 0) return null;
	const inputText = JSON.stringify(input, null, 2);
	return (
		<DetailBox label="入力" copyLabel="入力をコピー" copyValue={inputText}>
			<pre className={`select-text ${PRE_CLASS}`}>{inputText}</pre>
		</DetailBox>
	);
}

export function ToolHeaderRow({
	tools,
	children,
}: {
	tools: ToolUseRenderBlock[];
	children: ReactNode;
}) {
	return (
		<div className="group/tool-row flex min-w-0 items-center gap-1">
			{children}
			<span className="flex opacity-0 transition-opacity duration-150 group-hover/tool-row:opacity-100 focus-within:opacity-100">
				<CopyIconButton
					value={tools.map(toolCopyText).join("\n\n---\n\n")}
					label="ツール名・入力・出力をコピー"
				/>
			</span>
		</div>
	);
}

function SkippedNote() {
	return <div className="text-ui text-fg-3">考察中</div>;
}

export function ToolDetail({ tool }: { tool: ToolUseRenderBlock }) {
	const note = tool.status === "skipped" ? <SkippedNote /> : null;
	if (isBrowserTool(tool)) {
		// ラベルは入力の一部しか要約しないので、入力全体も見せる。browser_state は tool_result の
		// 出力抽出 (text ブロックのみ) に含まれないので、ここには出ない。
		return (
			<div className="pt-1.5 min-w-0 grid gap-2">
				<InputBox input={tool.input} />
				{tool.images && <MessageImages images={tool.images} />}
				{note}
				{tool.output && <OutputBox output={tool.output} />}
			</div>
		);
	}
	if (tool.name === "bash") {
		const command = typeof tool.input.command === "string" ? tool.input.command.trim() : "";
		return (
			<div className="pt-1.5 min-w-0 grid gap-2">
				{command && (
					<DetailBox label="Bash" copyLabel="入力をコピー" copyValue={command}>
						<pre className={PRE_CLASS}>
							{"$ "}
							{command}
						</pre>
					</DetailBox>
				)}
				{note}
				{tool.output && <OutputBox output={tool.output} />}
			</div>
		);
	}
	if (tool.name === "str_replace_based_edit_tool" && tool.input.command === "view") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		return (
			<div className="pt-1.5 min-w-0 grid gap-2">
				{path && <div className="font-mono wrap-anywhere text-caption text-fg-3">{path}</div>}
				{note}
				{tool.output && <OutputBox output={tool.output} />}
			</div>
		);
	}
	if (tool.name === "view_image") {
		const path = typeof tool.input.path === "string" ? tool.input.path : "";
		return (
			<div className="pt-1.5 min-w-0 grid gap-2">
				{path && <div className="font-mono wrap-anywhere text-caption text-fg-3">{path}</div>}
				{tool.images && <MessageImages images={tool.images} />}
				{note}
				{tool.output && <OutputBox output={tool.output} />}
			</div>
		);
	}
	if (tool.name === "str_replace_based_edit_tool" && tool.diff) {
		const counts = countDiffLines(tool.diff.hunks);
		return (
			<div className="pt-1.5 min-w-0 grid gap-2">
				{tool.diff.path && (
					<div className="flex min-w-0 items-center gap-1 font-mono text-caption text-fg-3">
						<DiffFileLink path={tool.diff.path} />
						<DiffLineStats added={counts.added} removed={counts.removed} />
					</div>
				)}
				<div className="min-w-0">
					<div className="mb-1 text-section font-semibold tracking-label uppercase text-fg-3">
						変更内容
					</div>
					<div className="max-h-[300px] overflow-y-auto">
						<DiffHunkList hunks={tool.diff.hunks} truncated={tool.diff.truncated} />
					</div>
				</div>
				{note}
				{tool.output && <OutputBox output={tool.output} />}
			</div>
		);
	}
	return (
		<div className="pt-1.5 min-w-0 grid gap-2">
			<InputBox input={tool.input} />
			{note}
			{tool.output && <OutputBox output={tool.output} />}
		</div>
	);
}

export function StatusDot({ status }: { status: ToolCallStatus }) {
	return (
		<CommonStatusDot
			tone={DOT_TONE[status]}
			pulse={status === "running"}
			testId="tool-call-status-bullet"
		/>
	);
}

export function ToolEntry({
	tool,
	expanded,
	animateExpand = true,
	onToggle,
}: {
	tool: ToolUseRenderBlock;
	expanded: boolean;
	animateExpand?: boolean;
	onToggle: () => void;
}) {
	const hasDetails = hasToolDetails(tool);
	return (
		<div className="min-w-0">
			<ToolHeaderRow tools={[tool]}>
				<button
					type="button"
					onClick={() => hasDetails && onToggle()}
					disabled={!hasDetails}
					className={`min-w-0 ${HEADER_BUTTON_CLASS}`}
				>
					<StatusDot status={tool.status} />
					<ToolEntryLabel tool={tool} />
					{hasDetails && <DisclosureChevron open={expanded} size={11} />}
				</button>
			</ToolHeaderRow>
			{expanded && (
				<ExpandIn animate={animateExpand}>
					<ToolDetail tool={tool} />
				</ExpandIn>
			)}
		</div>
	);
}
